import type { User as OidcUser } from 'oidc-client-ts'

import {
  authOidcDebugLog,
  authOidcSerializeError,
  authOidcSilentRenewErrorMayBenefitFromIframeFallback,
  authOidcUserSnapshotMeta,
} from '@/auth/authOidcDebugLog'
import { notifyAuthSessionLost } from '@/auth/authSessionLost'
import {
  shouldUseExchangeBackendRefresh,
  tryRenewExchangeSession,
} from '@/auth/keycloakExchangeSession'
import { getKeycloakUserManager } from '@/auth/keycloakUserManager'

export type EnsureOidcFreshSource = 'apollo' | 'isGranted'

let renewalInFlight: Promise<void> | null = null

/**
 * Single-flight silent renew for expired access tokens (Apollo link + isGranted).
 * Avoids parallel signinSilent storms when many requests hit an expired session.
 */
export async function ensureOidcUserFreshForApi(options: {
  source: EnsureOidcFreshSource
  /** GraphQL operation name when source is apollo */
  operationName: string | null
}): Promise<OidcUser | null> {
  const startedAt = Date.now()
  const um = getKeycloakUserManager()
  let user = await um.getUser()
  if (!user?.expired) {
    authOidcDebugLog('ensure_fresh_fast_path', {
      source: options.source,
      operation: options.operationName,
      durationMs: Date.now() - startedAt,
      ...authOidcUserSnapshotMeta(user),
    })
    return user
  }

  if (!renewalInFlight) {
    renewalInFlight = (async () => {
      const opName = options.operationName
      if (options.source === 'apollo') {
        authOidcDebugLog('apollo_context_access_token_expired', {
          operation: opName,
          ...authOidcUserSnapshotMeta(user),
        })
      } else {
        authOidcDebugLog('isGranted_access_token_expired', {
          ...authOidcUserSnapshotMeta(user),
        })
      }

      try {
        if (shouldUseExchangeBackendRefresh(user)) {
          try {
            const renewed = await tryRenewExchangeSession(um)
            if (renewed && !renewed.expired) {
              user = renewed
              if (options.source === 'apollo') {
                authOidcDebugLog('apollo_context_exchange_refresh_ok', {
                  operation: opName,
                  ...authOidcUserSnapshotMeta(user),
                })
              } else {
                authOidcDebugLog('isGranted_exchange_refresh_ok', {
                  ...authOidcUserSnapshotMeta(user),
                })
              }
            }
          } catch (ex) {
            if (options.source === 'apollo') {
              authOidcDebugLog('apollo_context_exchange_refresh_failed', {
                operation: opName,
                ...authOidcSerializeError(ex),
              })
            } else {
              authOidcDebugLog(
                'isGranted_exchange_refresh_failed',
                authOidcSerializeError(ex)
              )
            }
          }
        }

        if (!user || user.expired) {
          user = await um.signinSilent()
          if (options.source === 'apollo') {
            authOidcDebugLog('apollo_context_signinSilent_ok', {
              operation: opName,
              ...authOidcUserSnapshotMeta(user),
            })
          } else {
            authOidcDebugLog('isGranted_signinSilent_ok', {
              ...authOidcUserSnapshotMeta(user),
            })
          }
        }
      } catch (e) {
        if (options.source === 'apollo') {
          authOidcDebugLog('apollo_context_signinSilent_failed', {
            operation: opName,
            ...authOidcSerializeError(e),
          })
        } else {
          authOidcDebugLog(
            'isGranted_signinSilent_failed',
            authOidcSerializeError(e)
          )
        }

        if (authOidcSilentRenewErrorMayBenefitFromIframeFallback(e)) {
          try {
            user = await um.signinSilent({ forceIframeAuth: true })
            if (user?.access_token) {
              if (options.source === 'apollo') {
                authOidcDebugLog(
                  'apollo_context_signinSilent_iframe_fallback_ok',
                  {
                    operation: opName,
                    ...authOidcUserSnapshotMeta(user),
                  }
                )
              } else {
                authOidcDebugLog('isGranted_signinSilent_iframe_fallback_ok', {
                  ...authOidcUserSnapshotMeta(user),
                })
              }
            } else {
              user = null
              if (options.source === 'apollo') {
                authOidcDebugLog(
                  'apollo_context_signinSilent_iframe_fallback_empty',
                  {
                    operation: opName,
                  }
                )
                notifyAuthSessionLost('apollo_middleware_signinSilent_failed', {
                  operation: opName,
                })
              } else {
                notifyAuthSessionLost('isGranted_signinSilent_failed')
              }
            }
          } catch (e2) {
            user = null
            if (options.source === 'apollo') {
              authOidcDebugLog(
                'apollo_context_signinSilent_iframe_fallback_failed',
                {
                  operation: opName,
                  ...authOidcSerializeError(e2),
                }
              )
              notifyAuthSessionLost('apollo_middleware_signinSilent_failed', {
                operation: opName,
              })
            } else {
              authOidcDebugLog(
                'isGranted_signinSilent_iframe_fallback_failed',
                authOidcSerializeError(e2)
              )
              notifyAuthSessionLost('isGranted_signinSilent_failed')
            }
          }
        } else {
          user = null
          if (options.source === 'apollo') {
            notifyAuthSessionLost('apollo_middleware_signinSilent_failed', {
              operation: opName,
            })
          } else {
            notifyAuthSessionLost('isGranted_signinSilent_failed')
          }
        }
      }
    })().finally(() => {
      renewalInFlight = null
    })
  }

  await renewalInFlight
  const freshUser = await um.getUser()
  authOidcDebugLog('ensure_fresh_done', {
    source: options.source,
    operation: options.operationName,
    durationMs: Date.now() - startedAt,
    ...authOidcUserSnapshotMeta(freshUser),
  })
  return freshUser
}
